// Region classification for Osano's per-jurisdiction consent UI. Osano picks
// the *consent rules* (opt-in vs opt-out) per jurisdiction natively, but
// can't show a different *layout* per jurisdiction on its own — this module
// is the single source of truth for mapping an Osano jurisdiction code to
// the layout/blocking behavior Capterra (and, once verified, other brands)
// wants to show.
//
// See docs/privacy/osano-regional-presentation.md for the full design.

// GDPR country list, kept in parity with what OneTrust actually served on
// capterra.com (rule "gdpr countries", banner "Capterra Opt-in [From INTL]").
// Deliberately NOT the same as Osano's own `gdprApplies` flag or the broader
// `euCountryCodes` list used by the G2.com reference implementation this
// design is adapted from. See docs/privacy/osano-regional-presentation.md
// for the full design.
//
// 40 codes: the EEA, the UK, and French overseas territories, plus Åland,
// Monaco and San Marino.
//
// Switzerland (`ch`) is intentionally excluded — it fell under OneTrust's
// "global" rule, not the GDPR opt-in rule. Confirmed with Ashish Verma
// (2026-10-29): keep it out, for OneTrust parity.
export const GDPR_COUNTRIES = [
  'at',
  'ax',
  'be',
  'bg',
  'cy',
  'cz',
  'de',
  'dk',
  'ee',
  'es',
  'fi',
  'fr',
  'gb',
  'gf',
  'gp',
  'gr',
  'hr',
  'hu',
  'ie',
  'is',
  'it',
  'li',
  'lt',
  'lu',
  'lv',
  'mc',
  'mf',
  'mq',
  'mt',
  'nl',
  'no',
  'pl',
  'pt',
  're',
  'ro',
  'se',
  'si',
  'sk',
  'sm',
  'yt',
] as const;

// US states with a comprehensive consumer-privacy / opt-out law, as supplied
// by Ashish Verma (2026-10-29). This is a best-effort list pending legal's
// final sign-off — legal owns the source of truth (and Osano's dashboard
// "US State Level Banner Format" setting is the one that actually controls
// the opt-in/opt-out consent rule; this list only controls *layout*). Update
// this constant when legal confirms the final list.
export const US_OPTOUT_STATES = [
  'us-ca',
  'us-co',
  'us-ct',
  'us-de',
  'us-in',
  'us-ia',
  'us-ky',
  'us-md',
  'us-mn',
  'us-mt',
  'us-ne',
  'us-nh',
  'us-nj',
  'us-or',
  'us-ri',
  'us-tn',
  'us-tx',
  'us-ut',
  'us-va',
] as const;

export type OsanoRegion = 'eu' | 'us-optout' | 'us-none' | 'other';

// Two forms of the same piece of state, kept in one place so the React
// layer (OsanoConsentOverlay, via the dataset API) and the early-paint
// inline script + critical CSS (via setAttribute/attribute selectors) never
// drift apart. `dataset.osanoRegion` (camelCase) and the
// `data-osano-region` attribute (kebab-case) are the same underlying DOM
// attribute — this is a fixed, standard browser mapping, not something
// that can get out of sync at runtime; only the two string literals below
// need to stay in sync with each other.
export const OSANO_REGION_DATASET_KEY = 'osanoRegion';
export const OSANO_REGION_ATTRIBUTE = 'data-osano-region';

const GDPR_COUNTRY_SET: ReadonlySet<string> = new Set(GDPR_COUNTRIES);
const US_OPTOUT_STATE_SET: ReadonlySet<string> = new Set(US_OPTOUT_STATES);

/**
 * Classifies an Osano jurisdiction code (`Osano.cm.jurisdiction`, e.g.
 * `"de"`, `"gb"`, `"us-ca"`, `"us-tx"`) into one of four layout regions:
 *
 * - `eu`: a GDPR_COUNTRIES country code. Gets the blocking opt-in pop-up.
 * - `us-optout`: a US_OPTOUT_STATES state. Gets Osano's native opt-out bar.
 * - `us-none`: any other `us-*` jurisdiction. Gets no banner, widget only.
 * - `other`: everything else, including empty, unresolved, or unknown
 *   jurisdictions (confirmed with Ashish Verma, 2026-10-29: unknown location
 *   is treated as the non-blocking opt-out bar, same as "other countries" —
 *   not escalated to the stricter GDPR pop-up).
 *
 * Guards against non-string input, since `Osano.cm.jurisdiction` can be
 * empty, `undefined`, or otherwise unresolved.
 */
export const classifyOsanoRegion = (jurisdiction: unknown): OsanoRegion => {
  if (typeof jurisdiction !== 'string' || jurisdiction === '') {
    return 'other';
  }

  const normalized = jurisdiction.toLowerCase();
  const countryCode = normalized.split('-')[0];

  if (GDPR_COUNTRY_SET.has(countryCode)) {
    return 'eu';
  }

  if (countryCode === 'us') {
    return US_OPTOUT_STATE_SET.has(normalized) ? 'us-optout' : 'us-none';
  }

  return 'other';
};
